import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Edit, Trash2, Eye, Ship, Anchor, Package, Bell, RefreshCw, MapPin, AlertCircle, CheckCircle, Clock, ChevronRight, FileText, Waves } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Vessel, Berth, VesselOrderAllocation, VesselNotification, Order } from "@shared/schema";

const vesselSchema = z.object({
  vesselName: z.string().min(1, "Vessel name is required"),
  imoNumber: z.string().optional(),
  callSign: z.string().optional(),
  mmsiNumber: z.string().optional(),
  vesselType: z.string().optional(),
  grossTonnage: z.string().optional(),
  netTonnage: z.string().optional(),
  deadweightTonnage: z.string().optional(),
  lengthOverall: z.string().optional(),
  beam: z.string().optional(),
  draftLoaded: z.string().optional(),
  draftBallast: z.string().optional(),
  currentDraft: z.string().optional(),
  maxDraft: z.string().optional(),
  loadingType: z.string().optional(),
  numberOfHolds: z.string().optional(),
  numberOfHatches: z.string().optional(),
  loadingRate: z.string().optional(),
  flag: z.string().optional(),
  portOfRegistry: z.string().optional(),
  owner: z.string().optional(),
  operator: z.string().optional(),
  classification: z.string().optional(),
  yearBuilt: z.string().optional(),
  status: z.string().optional(),
  notes: z.string().optional(),
});

const berthSchema = z.object({
  berthCode: z.string().min(1, "Berth code is required"),
  berthName: z.string().min(1, "Berth name is required"),
  portName: z.string().optional(),
  terminalName: z.string().optional(),
  berthLength: z.string().optional(),
  maxDraft: z.string().optional(),
  maxVesselLength: z.string().optional(),
  maxVesselBeam: z.string().optional(),
  maxDeadweight: z.string().optional(),
  loadingType: z.string().optional(),
  numberOfCranes: z.string().optional(),
  numberOfConveyors: z.string().optional(),
  loadingCapacity: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  status: z.string().optional(),
  notes: z.string().optional(),
});

const allocationSchema = z.object({
  vesselId: z.string().min(1, "Vessel is required"),
  orderId: z.string().min(1, "Order is required"),
  stackId: z.string().optional(),
  berthId: z.string().optional(),
  allocatedQuantity: z.string().optional(),
  product: z.string().optional(),
  loadingType: z.string().optional(),
  loadingSequence: z.string().optional(),
  holdNumber: z.string().optional(),
  scheduledLoadStart: z.string().optional(),
  scheduledLoadEnd: z.string().optional(),
  status: z.string().optional(),
  notes: z.string().optional(),
});

type VesselFormData = z.infer<typeof vesselSchema>;
type BerthFormData = z.infer<typeof berthSchema>;
type AllocationFormData = z.infer<typeof allocationSchema>;

const vesselTypes = [
  "bulk_carrier",
  "container_ship",
  "tanker",
  "general_cargo",
  "ro_ro",
  "lng_carrier",
  "ore_carrier",
];

const loadingTypes = [
  "conveyor",
  "container_crane",
  "grab",
  "pump",
];

const vesselStatuses = [
  { value: "active", label: "Active", color: "bg-green-500/20 text-green-400" },
  { value: "inactive", label: "Inactive", color: "bg-gray-500/20 text-gray-400" },
  { value: "in_port", label: "In Port", color: "bg-blue-500/20 text-blue-400" },
  { value: "at_sea", label: "At Sea", color: "bg-cyan-500/20 text-cyan-400" },
  { value: "loading", label: "Loading", color: "bg-orange-500/20 text-orange-400" },
  { value: "discharging", label: "Discharging", color: "bg-yellow-500/20 text-yellow-400" },
];

const berthStatuses = [
  { value: "available", label: "Available", color: "bg-green-500/20 text-green-400" },
  { value: "occupied", label: "Occupied", color: "bg-red-500/20 text-red-400" },
  { value: "maintenance", label: "Maintenance", color: "bg-yellow-500/20 text-yellow-400" },
  { value: "reserved", label: "Reserved", color: "bg-blue-500/20 text-blue-400" },
];

const allocationStatuses = [
  { value: "pending", label: "Pending", color: "bg-gray-500/20 text-gray-400" },
  { value: "in_progress", label: "In Progress", color: "bg-orange-500/20 text-orange-400" },
  { value: "completed", label: "Completed", color: "bg-green-500/20 text-green-400" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-500/20 text-red-400" },
];

const notificationTypes = [
  { value: "eta_update", label: "ETA Update", icon: Clock },
  { value: "berthing", label: "Berthing", icon: Anchor },
  { value: "loading_start", label: "Loading Started", icon: Package },
  { value: "loading_complete", label: "Loading Complete", icon: CheckCircle },
  { value: "departure", label: "Departure", icon: Ship },
  { value: "draft_warning", label: "Draft Warning", icon: AlertCircle },
];

function getStatusBadge(status: string, statusList: { value: string; label: string; color: string }[]) {
  const found = statusList.find(s => s.value === status);
  return found ? (
    <Badge className={found.color}>{found.label}</Badge>
  ) : (
    <Badge className="bg-gray-500/20 text-gray-400">{status}</Badge>
  );
}

function formatVesselType(type: string | null | undefined): string {
  if (!type) return "-";
  return type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

export default function VesselManagement() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("vessels");
  const [searchTerm, setSearchTerm] = useState("");
  const [isVesselDialogOpen, setIsVesselDialogOpen] = useState(false);
  const [isBerthDialogOpen, setIsBerthDialogOpen] = useState(false);
  const [isAllocationDialogOpen, setIsAllocationDialogOpen] = useState(false);
  const [editingVessel, setEditingVessel] = useState<Vessel | null>(null);
  const [editingBerth, setEditingBerth] = useState<Berth | null>(null);
  const [editingAllocation, setEditingAllocation] = useState<VesselOrderAllocation | null>(null);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);

  const { data: vessels = [], isLoading: vesselsLoading } = useQuery<Vessel[]>({
    queryKey: ['/api/vessels'],
  });

  const { data: berths = [], isLoading: berthsLoading } = useQuery<Berth[]>({
    queryKey: ['/api/berths'],
  });

  const { data: allocations = [], isLoading: allocationsLoading } = useQuery<VesselOrderAllocation[]>({
    queryKey: ['/api/vessel-allocations'],
  });

  const { data: notifications = [], isLoading: notificationsLoading } = useQuery<VesselNotification[]>({
    queryKey: ['/api/vessel-notifications'],
  });

  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ['/api/orders'],
  });

  const vesselForm = useForm<VesselFormData>({
    resolver: zodResolver(vesselSchema),
    defaultValues: {
      vesselName: "",
      imoNumber: "",
      callSign: "",
      mmsiNumber: "",
      vesselType: "",
      grossTonnage: "",
      netTonnage: "",
      deadweightTonnage: "",
      lengthOverall: "",
      beam: "",
      draftLoaded: "",
      draftBallast: "",
      currentDraft: "",
      maxDraft: "",
      loadingType: "conveyor",
      numberOfHolds: "",
      numberOfHatches: "",
      loadingRate: "",
      flag: "",
      portOfRegistry: "",
      owner: "",
      operator: "",
      classification: "",
      yearBuilt: "",
      status: "active",
      notes: "",
    }
  });

  const berthForm = useForm<BerthFormData>({
    resolver: zodResolver(berthSchema),
    defaultValues: {
      berthCode: "",
      berthName: "",
      portName: "",
      terminalName: "",
      berthLength: "",
      maxDraft: "",
      maxVesselLength: "",
      maxVesselBeam: "",
      maxDeadweight: "",
      loadingType: "conveyor",
      numberOfCranes: "",
      numberOfConveyors: "",
      loadingCapacity: "",
      latitude: "",
      longitude: "",
      status: "available",
      notes: "",
    }
  });

  const allocationForm = useForm<AllocationFormData>({
    resolver: zodResolver(allocationSchema),
    defaultValues: {
      vesselId: "",
      orderId: "",
      stackId: "",
      berthId: "",
      allocatedQuantity: "",
      product: "",
      loadingType: "conveyor",
      loadingSequence: "1",
      holdNumber: "",
      scheduledLoadStart: "",
      scheduledLoadEnd: "",
      status: "pending",
      notes: "",
    }
  });

  const createVesselMutation = useMutation({
    mutationFn: (data: VesselFormData) => apiRequest("POST", "/api/vessels", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessels'] });
      toast({ title: "Success", description: "Vessel created successfully" });
      setIsVesselDialogOpen(false);
      vesselForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create vessel", variant: "destructive" });
    }
  });

  const updateVesselMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VesselFormData }) => 
      apiRequest("PUT", `/api/vessels/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessels'] });
      toast({ title: "Success", description: "Vessel updated successfully" });
      setIsVesselDialogOpen(false);
      setEditingVessel(null);
      vesselForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update vessel", variant: "destructive" });
    }
  });

  const deleteVesselMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/vessels/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessels'] });
      toast({ title: "Success", description: "Vessel deleted" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete vessel", variant: "destructive" });
    }
  });

  const createBerthMutation = useMutation({
    mutationFn: (data: BerthFormData) => apiRequest("POST", "/api/berths", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/berths'] });
      toast({ title: "Success", description: "Berth created successfully" });
      setIsBerthDialogOpen(false);
      berthForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create berth", variant: "destructive" });
    }
  });

  const updateBerthMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: BerthFormData }) => 
      apiRequest("PUT", `/api/berths/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/berths'] });
      toast({ title: "Success", description: "Berth updated successfully" });
      setIsBerthDialogOpen(false);
      setEditingBerth(null);
      berthForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update berth", variant: "destructive" });
    }
  });

  const deleteBerthMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/berths/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/berths'] });
      toast({ title: "Success", description: "Berth deleted" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete berth", variant: "destructive" });
    }
  });

  const createAllocationMutation = useMutation({
    mutationFn: (data: AllocationFormData) => apiRequest("POST", "/api/vessel-allocations", {
      ...data,
      vesselId: parseInt(data.vesselId),
      orderId: parseInt(data.orderId),
      stackId: data.stackId ? parseInt(data.stackId) : undefined,
      berthId: data.berthId ? parseInt(data.berthId) : undefined,
      loadingSequence: data.loadingSequence ? parseInt(data.loadingSequence) : 1,
      holdNumber: data.holdNumber ? parseInt(data.holdNumber) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessel-allocations'] });
      toast({ title: "Success", description: "Allocation created successfully" });
      setIsAllocationDialogOpen(false);
      allocationForm.reset();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create allocation", variant: "destructive" });
    }
  });

  const markNotificationReadMutation = useMutation({
    mutationFn: (id: number) => apiRequest("PATCH", `/api/vessel-notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessel-notifications'] });
    }
  });

  const dismissNotificationMutation = useMutation({
    mutationFn: (id: number) => apiRequest("PATCH", `/api/vessel-notifications/${id}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/vessel-notifications'] });
      toast({ title: "Success", description: "Notification dismissed" });
    }
  });

  const filteredVessels = vessels.filter(vessel =>
    vessel.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vessel.imoNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vessel.callSign?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredBerths = berths.filter(berth =>
    berth.berthName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    berth.berthCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    berth.portName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditVessel = (vessel: Vessel) => {
    setEditingVessel(vessel);
    vesselForm.reset({
      vesselName: vessel.vesselName || "",
      imoNumber: vessel.imoNumber || "",
      callSign: vessel.callSign || "",
      mmsiNumber: vessel.mmsiNumber || "",
      vesselType: vessel.vesselType || "",
      grossTonnage: vessel.grossTonnage || "",
      netTonnage: vessel.netTonnage || "",
      deadweightTonnage: vessel.deadweightTonnage || "",
      lengthOverall: vessel.lengthOverall || "",
      beam: vessel.beam || "",
      draftLoaded: vessel.draftLoaded || "",
      draftBallast: vessel.draftBallast || "",
      currentDraft: vessel.currentDraft || "",
      maxDraft: vessel.maxDraft || "",
      loadingType: vessel.loadingType || "conveyor",
      numberOfHolds: vessel.numberOfHolds?.toString() || "",
      numberOfHatches: vessel.numberOfHatches?.toString() || "",
      loadingRate: vessel.loadingRate || "",
      flag: vessel.flag || "",
      portOfRegistry: vessel.portOfRegistry || "",
      owner: vessel.owner || "",
      operator: vessel.operator || "",
      classification: vessel.classification || "",
      yearBuilt: vessel.yearBuilt?.toString() || "",
      status: vessel.status || "active",
      notes: vessel.notes || "",
    });
    setIsVesselDialogOpen(true);
  };

  const handleEditBerth = (berth: Berth) => {
    setEditingBerth(berth);
    berthForm.reset({
      berthCode: berth.berthCode || "",
      berthName: berth.berthName || "",
      portName: berth.portName || "",
      terminalName: berth.terminalName || "",
      berthLength: berth.berthLength || "",
      maxDraft: berth.maxDraft || "",
      maxVesselLength: berth.maxVesselLength || "",
      maxVesselBeam: berth.maxVesselBeam || "",
      maxDeadweight: berth.maxDeadweight || "",
      loadingType: berth.loadingType || "conveyor",
      numberOfCranes: berth.numberOfCranes?.toString() || "",
      numberOfConveyors: berth.numberOfConveyors?.toString() || "",
      loadingCapacity: berth.loadingCapacity || "",
      latitude: berth.latitude || "",
      longitude: berth.longitude || "",
      status: berth.status || "available",
      notes: berth.notes || "",
    });
    setIsBerthDialogOpen(true);
  };

  const onVesselSubmit = (data: VesselFormData) => {
    if (editingVessel) {
      updateVesselMutation.mutate({ id: editingVessel.id, data });
    } else {
      createVesselMutation.mutate(data);
    }
  };

  const onBerthSubmit = (data: BerthFormData) => {
    if (editingBerth) {
      updateBerthMutation.mutate({ id: editingBerth.id, data });
    } else {
      createBerthMutation.mutate(data);
    }
  };

  const onAllocationSubmit = (data: AllocationFormData) => {
    createAllocationMutation.mutate(data);
  };

  const unreadNotifications = notifications.filter(n => !n.isRead && !n.isDismissed);

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]" data-testid="text-page-title">
            Vessel Management
          </h1>
          <p className="text-muted-foreground">Manage vessels, berths, order allocations, and notifications</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {unreadNotifications.length > 0 && (
            <Badge className="bg-orange-500/20 text-orange-400">
              <Bell className="w-3 h-3 mr-1" />
              {unreadNotifications.length} new
            </Badge>
          )}
        </div>
      </div>

      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="grid grid-cols-4 w-full max-w-2xl bg-[hsl(215,28%,14%)]">
              <TabsTrigger value="vessels" className="flex items-center gap-2" data-testid="tab-vessels">
                <Ship className="w-4 h-4" />
                <span className="hidden sm:inline">Vessels</span>
              </TabsTrigger>
              <TabsTrigger value="berths" className="flex items-center gap-2" data-testid="tab-berths">
                <Anchor className="w-4 h-4" />
                <span className="hidden sm:inline">Berths</span>
              </TabsTrigger>
              <TabsTrigger value="allocations" className="flex items-center gap-2" data-testid="tab-allocations">
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">Allocations</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-2 relative" data-testid="tab-notifications">
                <Bell className="w-4 h-4" />
                <span className="hidden sm:inline">Notifications</span>
                {unreadNotifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotifications.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent className="pt-6">
            <TabsContent value="vessels" className="mt-0 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search vessels..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]"
                    data-testid="input-search-vessels"
                  />
                </div>
                <Button
                  onClick={() => { setEditingVessel(null); vesselForm.reset(); setIsVesselDialogOpen(true); }}
                  className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
                  data-testid="button-add-vessel"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vessel
                </Button>
              </div>

              {vesselsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 bg-muted" />
                  ))}
                </div>
              ) : filteredVessels.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Ship className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No vessels found</p>
                  <p className="text-sm">Add your first vessel to get started</p>
                </div>
              ) : (
                <div className="rounded-md border border-[hsl(215,28%,18%)] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[hsl(215,28%,12%)] hover:bg-[hsl(215,28%,12%)]">
                        <TableHead className="text-[hsl(187,100%,42%)]">Vessel</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">IMO / Call Sign</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Type</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Draft (m)</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Loading</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Status</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVessels.map((vessel) => (
                        <TableRow key={vessel.id} className="hover:bg-[hsl(215,28%,12%)]" data-testid={`row-vessel-${vessel.id}`}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{vessel.vesselName}</div>
                              <div className="text-sm text-muted-foreground">{vessel.flag || "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>IMO: {vessel.imoNumber || "-"}</div>
                              <div className="text-muted-foreground">{vessel.callSign || "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>{formatVesselType(vessel.vesselType)}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>Current: {vessel.currentDraft || "-"}</div>
                              <div className="text-muted-foreground">Max: {vessel.maxDraft || "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {vessel.loadingType?.replace(/_/g, " ") || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(vessel.status || "active", vesselStatuses)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setSelectedVessel(vessel)}
                                data-testid={`button-view-vessel-${vessel.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditVessel(vessel)}
                                data-testid={`button-edit-vessel-${vessel.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteVesselMutation.mutate(vessel.id)}
                                data-testid={`button-delete-vessel-${vessel.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="berths" className="mt-0 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search berths..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]"
                    data-testid="input-search-berths"
                  />
                </div>
                <Button
                  onClick={() => { setEditingBerth(null); berthForm.reset(); setIsBerthDialogOpen(true); }}
                  className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
                  data-testid="button-add-berth"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Berth
                </Button>
              </div>

              {berthsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 bg-muted" />
                  ))}
                </div>
              ) : filteredBerths.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Anchor className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No berths found</p>
                  <p className="text-sm">Add your first berth to get started</p>
                </div>
              ) : (
                <div className="rounded-md border border-[hsl(215,28%,18%)] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[hsl(215,28%,12%)] hover:bg-[hsl(215,28%,12%)]">
                        <TableHead className="text-[hsl(187,100%,42%)]">Berth</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Port / Terminal</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Specifications</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Loading Equipment</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Status</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBerths.map((berth) => (
                        <TableRow key={berth.id} className="hover:bg-[hsl(215,28%,12%)]" data-testid={`row-berth-${berth.id}`}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{berth.berthName}</div>
                              <div className="text-sm text-muted-foreground">{berth.berthCode}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{berth.portName || "-"}</div>
                              <div className="text-muted-foreground">{berth.terminalName || "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>Length: {berth.berthLength ? `${berth.berthLength}m` : "-"}</div>
                              <div className="text-muted-foreground">Max Draft: {berth.maxDraft ? `${berth.maxDraft}m` : "-"}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="capitalize">{berth.loadingType?.replace(/_/g, " ") || "-"}</div>
                              <div className="text-muted-foreground">
                                {berth.loadingCapacity ? `${berth.loadingCapacity} t/hr` : "-"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(berth.status || "available", berthStatuses)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEditBerth(berth)}
                                data-testid={`button-edit-berth-${berth.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => deleteBerthMutation.mutate(berth.id)}
                                data-testid={`button-delete-berth-${berth.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="allocations" className="mt-0 space-y-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div>
                  <CardTitle className="text-[hsl(187,100%,42%)]">Order Allocations</CardTitle>
                  <CardDescription>Link orders to vessels for loading operations</CardDescription>
                </div>
                <Button
                  onClick={() => { setEditingAllocation(null); allocationForm.reset(); setIsAllocationDialogOpen(true); }}
                  className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium"
                  data-testid="button-add-allocation"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Allocation
                </Button>
              </div>

              {allocationsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 bg-muted" />
                  ))}
                </div>
              ) : allocations.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No allocations found</p>
                  <p className="text-sm">Create an allocation to link an order to a vessel</p>
                </div>
              ) : (
                <div className="rounded-md border border-[hsl(215,28%,18%)] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[hsl(215,28%,12%)] hover:bg-[hsl(215,28%,12%)]">
                        <TableHead className="text-[hsl(187,100%,42%)]">Vessel</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Order</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Product</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Quantity</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Loading</TableHead>
                        <TableHead className="text-[hsl(187,100%,42%)]">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocations.map((allocation) => {
                        const vessel = vessels.find(v => v.id === allocation.vesselId);
                        const order = orders.find(o => o.id === allocation.orderId);
                        const berth = berths.find(b => b.id === allocation.berthId);
                        return (
                          <TableRow key={allocation.id} className="hover:bg-[hsl(215,28%,12%)]" data-testid={`row-allocation-${allocation.id}`}>
                            <TableCell>
                              <div className="font-medium">{vessel?.vesselName || `Vessel #${allocation.vesselId}`}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{order?.orderNumber || `Order #${allocation.orderId}`}</div>
                                <div className="text-muted-foreground">{order?.clientName || "-"}</div>
                              </div>
                            </TableCell>
                            <TableCell>{allocation.product || "-"}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{allocation.loadedQuantity || 0} / {allocation.allocatedQuantity || 0}</div>
                                <div className="text-muted-foreground">{allocation.unit}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="capitalize">{allocation.loadingType?.replace(/_/g, " ") || "-"}</div>
                                <div className="text-muted-foreground">
                                  {berth?.berthName || (allocation.berthId ? `Berth #${allocation.berthId}` : "-")}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(allocation.status || "pending", allocationStatuses)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="notifications" className="mt-0 space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-[hsl(187,100%,42%)]">Notifications</CardTitle>
                  <CardDescription>Vessel operation alerts and updates</CardDescription>
                </div>
              </div>

              {notificationsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 bg-muted" />
                  ))}
                </div>
              ) : notifications.filter(n => !n.isDismissed).length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No notifications</p>
                  <p className="text-sm">You're all caught up</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.filter(n => !n.isDismissed).map((notification) => {
                    const vessel = vessels.find(v => v.id === notification.vesselId);
                    const typeInfo = notificationTypes.find(t => t.value === notification.type);
                    const IconComponent = typeInfo?.icon || Bell;
                    return (
                      <Card 
                        key={notification.id} 
                        className={`bg-[hsl(215,28%,12%)] border-[hsl(215,28%,18%)] ${!notification.isRead ? 'border-l-4 border-l-[hsl(24.6,95%,53%)]' : ''}`}
                        data-testid={`notification-${notification.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-2 rounded-md ${notification.priority === 'urgent' ? 'bg-red-500/20' : notification.priority === 'high' ? 'bg-orange-500/20' : 'bg-[hsl(215,28%,16%)]'}`}>
                              <IconComponent className={`w-5 h-5 ${notification.priority === 'urgent' ? 'text-red-400' : notification.priority === 'high' ? 'text-orange-400' : 'text-[hsl(187,100%,42%)]'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium">{notification.title}</h4>
                                {notification.priority && notification.priority !== 'normal' && (
                                  <Badge className={notification.priority === 'urgent' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}>
                                    {notification.priority}
                                  </Badge>
                                )}
                                {!notification.isRead && (
                                  <Badge className="bg-blue-500/20 text-blue-400">New</Badge>
                                )}
                              </div>
                              {vessel && (
                                <p className="text-sm text-muted-foreground">{vessel.vesselName}</p>
                              )}
                              {notification.message && (
                                <p className="text-sm mt-1">{notification.message}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                {notification.createdAt ? new Date(notification.createdAt).toLocaleString() : "-"}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              {!notification.isRead && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => markNotificationReadMutation.mutate(notification.id)}
                                  data-testid={`button-mark-read-${notification.id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => dismissNotificationMutation.mutate(notification.id)}
                                data-testid={`button-dismiss-${notification.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      <Dialog open={isVesselDialogOpen} onOpenChange={setIsVesselDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">
              {editingVessel ? "Edit Vessel" : "Add Vessel"}
            </DialogTitle>
            <DialogDescription>
              Enter the vessel details below
            </DialogDescription>
          </DialogHeader>
          <Form {...vesselForm}>
            <form onSubmit={vesselForm.handleSubmit(onVesselSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={vesselForm.control}
                  name="vesselName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Vessel Name *</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-vessel-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="imoNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">IMO Number</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-imo-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="callSign"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Call Sign</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-call-sign" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="vesselType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Vessel Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-vessel-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {vesselTypes.map(type => (
                            <SelectItem key={type} value={type}>{formatVesselType(type)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="grossTonnage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Gross Tonnage</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-gross-tonnage" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="deadweightTonnage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Deadweight (DWT)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-dwt" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="currentDraft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Current Draft (m)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-current-draft" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="maxDraft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Max Draft (m)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-max-draft" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="loadingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Loading Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-loading-type">
                            <SelectValue placeholder="Select loading type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingTypes.map(type => (
                            <SelectItem key={type} value={type}>{type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="numberOfHolds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Number of Holds</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-holds" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="flag"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Flag</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-flag" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="owner"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Owner</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-owner" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {vesselStatuses.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={vesselForm.control}
                  name="classification"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Classification</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="ABS, DNV GL, Lloyd's..." className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-classification" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={vesselForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[hsl(187,100%,42%)]">Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-notes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsVesselDialogOpen(false)} data-testid="button-cancel">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium" data-testid="button-save-vessel">
                  {editingVessel ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isBerthDialogOpen} onOpenChange={setIsBerthDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">
              {editingBerth ? "Edit Berth" : "Add Berth"}
            </DialogTitle>
            <DialogDescription>
              Enter the berth details below
            </DialogDescription>
          </DialogHeader>
          <Form {...berthForm}>
            <form onSubmit={berthForm.handleSubmit(onBerthSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={berthForm.control}
                  name="berthCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Berth Code *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="B01" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-berth-code" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="berthName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Berth Name *</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-berth-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="portName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Port Name</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-port-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="terminalName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Terminal Name</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-terminal-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="berthLength"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Berth Length (m)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-berth-length" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="maxDraft"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Max Draft (m)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-berth-max-draft" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="loadingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Loading Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-berth-loading-type">
                            <SelectValue placeholder="Select loading type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingTypes.map(type => (
                            <SelectItem key={type} value={type}>{type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="loadingCapacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Loading Capacity (t/hr)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-loading-capacity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={berthForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-berth-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {berthStatuses.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={berthForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[hsl(187,100%,42%)]">Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-berth-notes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsBerthDialogOpen(false)} data-testid="button-cancel-berth">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium" data-testid="button-save-berth">
                  {editingBerth ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isAllocationDialogOpen} onOpenChange={setIsAllocationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">
              Create Order Allocation
            </DialogTitle>
            <DialogDescription>
              Link an order to a vessel for loading operations
            </DialogDescription>
          </DialogHeader>
          <Form {...allocationForm}>
            <form onSubmit={allocationForm.handleSubmit(onAllocationSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={allocationForm.control}
                  name="vesselId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Vessel *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-vessel">
                            <SelectValue placeholder="Select vessel" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {vessels.map(vessel => (
                            <SelectItem key={vessel.id} value={vessel.id.toString()}>{vessel.vesselName}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="orderId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Order *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-order">
                            <SelectValue placeholder="Select order" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {orders.map(order => (
                            <SelectItem key={order.id} value={order.id.toString()}>
                              {order.orderNumber} - {order.clientName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="berthId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Berth</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-berth">
                            <SelectValue placeholder="Select berth" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {berths.map(berth => (
                            <SelectItem key={berth.id} value={berth.id.toString()}>{berth.berthName}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="product"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Product</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-product" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="allocatedQuantity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Allocated Quantity (tons)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-allocated-quantity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="loadingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Loading Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-alloc-loading-type">
                            <SelectValue placeholder="Select loading type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingTypes.map(type => (
                            <SelectItem key={type} value={type}>{type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="holdNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Hold Number</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-hold-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={allocationForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[hsl(187,100%,42%)]">Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="select-alloc-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {allocationStatuses.map(status => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={allocationForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[hsl(187,100%,42%)]">Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="bg-[hsl(215,28%,14%)] border-[hsl(215,28%,18%)]" data-testid="input-alloc-notes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAllocationDialogOpen(false)} data-testid="button-cancel-alloc">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,85%,50%)] text-black font-medium" data-testid="button-save-alloc">
                  Create Allocation
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedVessel} onOpenChange={() => setSelectedVessel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
              <Ship className="w-5 h-5" />
              {selectedVessel?.vesselName}
            </DialogTitle>
            <DialogDescription>
              Vessel details and specifications
            </DialogDescription>
          </DialogHeader>
          {selectedVessel && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(selectedVessel.status || "active", vesselStatuses)}
                <Badge variant="outline">{selectedVessel.vesselCode}</Badge>
                {selectedVessel.flag && <Badge variant="outline">{selectedVessel.flag}</Badge>}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Identification</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">IMO:</span><span>{selectedVessel.imoNumber || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Call Sign:</span><span>{selectedVessel.callSign || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">MMSI:</span><span>{selectedVessel.mmsiNumber || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span>{formatVesselType(selectedVessel.vesselType)}</span></div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Tonnage</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Gross:</span><span>{selectedVessel.grossTonnage || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Net:</span><span>{selectedVessel.netTonnage || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">DWT:</span><span>{selectedVessel.deadweightTonnage || "-"}</span></div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Dimensions & Draft</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Length:</span><span>{selectedVessel.lengthOverall ? `${selectedVessel.lengthOverall}m` : "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Beam:</span><span>{selectedVessel.beam ? `${selectedVessel.beam}m` : "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Current Draft:</span><span>{selectedVessel.currentDraft ? `${selectedVessel.currentDraft}m` : "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Max Draft:</span><span>{selectedVessel.maxDraft ? `${selectedVessel.maxDraft}m` : "-"}</span></div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Loading Configuration</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span className="capitalize">{selectedVessel.loadingType?.replace(/_/g, " ") || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Holds:</span><span>{selectedVessel.numberOfHolds || "-"}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Rate:</span><span>{selectedVessel.loadingRate ? `${selectedVessel.loadingRate} t/hr` : "-"}</span></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Ownership & Classification</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Owner:</span><span>{selectedVessel.owner || "-"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Operator:</span><span>{selectedVessel.operator || "-"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Classification:</span><span>{selectedVessel.classification || "-"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Year Built:</span><span>{selectedVessel.yearBuilt || "-"}</span></div>
                </div>
              </div>
              
              {selectedVessel.notes && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Notes</h4>
                  <p className="text-sm">{selectedVessel.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
